const MENTION_PATTERN = /(?:^|[^\w@])@(\w[\w.-]*)/g;
/** The separators a dotted handle may be shortened at (see `mentionPrefixes`). */
const HANDLE_SEPARATORS = /[.-]/;
/**
 * Tokenize every mention candidate in `body`, in order, with positions.
 * Trailing separators are trimmed so "see @lee." yields `lee`, not `lee.`.
 */
export function extractMentionTokens(body) {
    const out = [];
    // Fresh statefulness per call: `matchAll` never mutates the shared regex's
    // lastIndex, so the module-level pattern is safe to reuse.
    for (const match of body.matchAll(MENTION_PATTERN)) {
        const raw = match[1];
        const token = raw.replace(/[.-]+$/, "");
        if (token === "")
            continue;
        // match[0] is an optional single prefix char + "@" + raw. The prefix class
        // excludes "@", so the first "@" in match[0] IS the separator.
        const start = (match.index ?? 0) + match[0].indexOf("@");
        out.push({ token, start });
    }
    return out;
}
/**
 * Extract mention tokens (without the `@`) from a comment body, de-duplicated
 * (case-insensitively), in order of first appearance. Directory-free: this is
 * the RAW token list, so a dotted token comes back whole. Callers that need
 * to know which real person a token names use `resolveMentions`.
 */
export function extractMentions(body) {
    const seen = new Set();
    const out = [];
    for (const { token } of extractMentionTokens(body)) {
        const key = token.toLowerCase();
        if (!seen.has(key)) {
            seen.add(key);
            out.push(token);
        }
    }
    return out;
}
export const EMPTY_MENTION_DIRECTORY = {
    byHandle: new Map(),
    targets: [],
};
/** Strip an email/name down to the handle grammar: `\w`, `.`, `-`, lower-cased. */
function sanitizeHandle(raw) {
    return raw
        .toLowerCase()
        .replace(/[^\w.-]/g, "")
        .replace(/^[.-]+/, "")
        .replace(/[.-]+$/, "");
}
/** "kim.lee@corp.example" → `{ local: "kim.lee", domain: "corp" }`. */
function splitEmail(email) {
    const at = email.lastIndexOf("@");
    if (at < 0)
        return { local: email, domain: "" };
    return {
        local: email.slice(0, at),
        domain: (email.slice(at + 1).split(".")[0] ?? ""),
    };
}
/**
 * The handle for one member. The bare email local part when it is unique in
 * the roster; otherwise qualified with the domain's first label
 * ("kim@a.com" + "kim@b.com" → `kim_a` / `kim_b`).
 *
 * The qualified form is derived ONLY from the member's own email, so it is
 * stable as the roster grows: a third `kim@c.com` joining makes `kim_c` and
 * leaves the other two alone. A handle that shifted between people as members
 * were added would silently re-point old comment text at the wrong person.
 */
export function mentionHandleFor(email, qualified) {
    const { local, domain } = splitEmail(email);
    const base = sanitizeHandle(local);
    if (!qualified)
        return base || sanitizeHandle(email);
    const suffix = sanitizeHandle(domain);
    return suffix ? `${base}_${suffix}` : base;
}
/**
 * Build the handle index for a roster (`GET /api/team`).
 *
 * Two members that still collide after qualification are BOTH dropped from
 * `byHandle` — their handle resolves to nobody rather than to a coin flip.
 * They stay in `targets` (the picker still lists them) so the ambiguity is
 * visible instead of silent; picking one inserts a handle that highlights but
 * does not notify, which is the safe failure.
 */
export function buildMentionDirectory(members) {
    const byLocal = new Map();
    for (const member of members) {
        const base = mentionHandleFor(member.email, false);
        byLocal.set(base, (byLocal.get(base) ?? 0) + 1);
    }
    const targets = members.map((member) => ({
        uid: member.uid,
        email: member.email,
        displayName: member.displayName || member.email || member.uid,
        handle: mentionHandleFor(member.email, (byLocal.get(mentionHandleFor(member.email, false)) ?? 0) > 1),
        avatarUrl: member.avatarUrl,
    }));
    targets.sort((a, b) => a.displayName.localeCompare(b.displayName));
    const counts = new Map();
    for (const target of targets) {
        if (target.handle)
            counts.set(target.handle, (counts.get(target.handle) ?? 0) + 1);
    }
    const byHandle = new Map();
    for (const target of targets) {
        if (target.handle && counts.get(target.handle) === 1)
            byHandle.set(target.handle, target);
    }
    return { byHandle, targets };
}
/* ------------------------------------------------------------------ */
/* Resolution                                                          */
/* ------------------------------------------------------------------ */
/**
 * The prefixes of `token` that could be a handle, LONGEST FIRST: the whole
 * token, then each prefix ending just before a `.`/`-`. Underscores are not
 * split on — they are ordinary handle characters in an email local part.
 */
function mentionPrefixes(token) {
    const out = [token];
    for (let i = token.length - 1; i > 0; i--) {
        if (HANDLE_SEPARATORS.test(token.charAt(i)))
            out.push(token.slice(0, i));
    }
    return out;
}
/** The leading word-character run of a token — the pre-dotted-handle extent. */
export function mentionWordPrefix(token) {
    return /^\w+/.exec(token)?.[0] ?? token;
}
/**
 * Resolve one raw token to a person, longest matching prefix wins. Returns the
 * matched handle too, because it is shorter than the token when the trailing
 * text was ordinary prose ("@kim.We" → handle "kim").
 */
export function resolveMentionToken(token, directory) {
    const lower = token.toLowerCase();
    for (const prefix of mentionPrefixes(lower)) {
        const target = directory.byHandle.get(prefix);
        if (target)
            return { handle: prefix, target };
    }
    return null;
}
/**
 * Every real person tagged in `body`, de-duplicated by email, in order of
 * first appearance. This is what the notification fan-out consumes — unknown
 * tokens are not people and never produce a notification.
 */
export function resolveMentions(body, directory) {
    const seen = new Set();
    const out = [];
    for (const { token } of extractMentionTokens(body)) {
        const hit = resolveMentionToken(token, directory);
        if (!hit || seen.has(hit.target.email))
            continue;
        seen.add(hit.target.email);
        out.push(hit.target);
    }
    return out;
}
/**
 * Split a comment body into plain/mention segments for F-54 highlighting.
 * Shares its tokenizer with `extractMentions`, so the highlight and the
 * notification can never disagree about what a mention is.
 *
 * A token that resolves highlights exactly its handle; one that does not
 * highlights only its leading word-character run, which is what the
 * word-only grammar produced before dotted handles existed.
 */
export function mentionSegments(body, directory = EMPTY_MENTION_DIRECTORY) {
    const out = [];
    let cursor = 0;
    for (const { token, start } of extractMentionTokens(body)) {
        // A previous segment may already cover this "@" (never with the current
        // grammar, but slicing backwards would corrupt the output if it did).
        if (start < cursor)
            continue;
        const hit = resolveMentionToken(token, directory);
        const shown = hit ? hit.handle.length : mentionWordPrefix(token).length;
        if (start > cursor)
            out.push({ text: body.slice(cursor, start), mention: false });
        const segment = {
            // Sliced from the body, so the author's original casing survives.
            text: body.slice(start, start + shown + 1),
            mention: true,
        };
        if (hit)
            segment.target = hit.target;
        out.push(segment);
        cursor = start + shown + 1;
    }
    if (cursor < body.length)
        out.push({ text: body.slice(cursor), mention: false });
    return out;
}
//# sourceMappingURL=mentions.js.map